import { useState, type FormEvent } from "react";

const FORM_ID = "db50q2ek6jkc2oct4at0";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db50q2ek6jkc2oct4at0";

type Status = "idle" | "loading" | "success" | "error";

export default function Newsletter() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    formData.delete("website_alt");

    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，热瓜有更新就会第一时间提醒你。");
      form.reset();
      return;
    }

    const email = String(formData.get("email") ?? "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("请填写一个有效的邮箱地址，方便我们把更新发给你。");
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;

      if (response.ok && code === "OK") {
        setStatus("success");
        setMessage("订阅成功，热瓜有更新就会第一时间提醒你。");
        form.reset();
        return;
      }

      if (String(serverMsg).toLowerCase().includes("spam")) {
        setStatus("error");
        setMessage("提交未通过校验，请稍后重试，或更换邮箱地址再试一次。");
        return;
      }

      setStatus("error");
      setMessage(serverMsg || "提交失败，请稍后重试。");
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
      <div className="relative rounded-lg overflow-hidden border border-background-200">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20dark%20gradient%20backdrop%20with%20blurred%20gold%20and%20crimson%20light%20spots%20and%20soft%20bokeh%20particles%2C%20no%20people%2C%20cinematic%20atmospheric%20texture%2C%20editorial%20background&width=1600&height=700&seq=newsletter-gossip&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/75 to-foreground-950/60" />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center px-6 md:px-12 py-10 md:py-14">
          <div>
            <span className="font-label text-sm text-accent-300">SUBSCRIBE · 订阅吃瓜</span>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold text-background-50">
              每天一封，热瓜不漏
            </h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-background-100/85">
              留下邮箱，我们会在每天更新后把当日讨论度最高的爆料清单发给你。只发汇总，不推销、不打扰。
            </p>
            <ul className="mt-5 space-y-2 text-sm text-background-100/85">
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-accent-400" />
                每日一条热度榜单摘要
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-accent-400" />
                重要事件的后续进展提醒
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-accent-400" />
                随时可退订，不保留多余信息
              </li>
            </ul>
          </div>

          <div className="bg-background-50 rounded-lg p-6 md:p-8">
            <form
              id={FORM_ID}
              data-readdy-form
              onSubmit={handleSubmit}
              noValidate
              className="flex flex-col gap-4"
            >
              <div>
                <label
                  htmlFor="subscribe-email"
                  className="block text-sm font-medium text-foreground-800 mb-2"
                >
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  placeholder="you@example.com"
                  className="w-full bg-background-100 border border-background-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-guest-extra"
              />

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium px-5 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                {status === "loading" ? "提交中…" : "订阅吃瓜邮件"}
              </button>

              {status === "success" ? (
                <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0" />
                  {message}
                </p>
              ) : null}
              {status === "error" ? (
                <p className="flex items-start gap-2 text-sm text-secondary-900 bg-secondary-50 border border-secondary-200 rounded-md px-3.5 py-2.5">
                  <i className="ri-error-warning-line mt-0.5 shrink-0" />
                  {message}
                </p>
              ) : null}

              <p className="text-xs text-foreground-400">
                提交即表示你同意我们仅将邮箱用于发送站内更新汇总。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}